import { useState } from 'react'
import { ApiError } from '@/lib/api'
import { auditLogGridContract } from '@/modules/data-grid'
import { formatDateTime } from '@/lib/format-date'
import { AUDIT_PERMISSIONS } from '@/lib/permissions'
import { usePermissions } from '@/lib/use-permissions'
import { useLoadMore } from '@/lib/use-load-more'
import { Button } from '@/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@/ui/dialog'
import { LoadingNotice } from '@/ui/loading-notice'
import { LoadMoreButton } from '@/ui/load-more-button'
import { RetryNotice } from '@/ui/retry-notice'
import { describeActor, type ActivityEntryBase } from '../../api/audit-api'

import { useUserAuditHistory } from './user-audit-log-queries'

export function UserAuditLog({ entityId, userId, userName }: { entityId: string; userId: string; userName: string }) {
  const { can } = usePermissions()
  const [open, setOpen] = useState(false)
  if (!can(AUDIT_PERMISSIONS.read)) return null
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Audit log</Button>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogTitle>Audit log for {userName}</DialogTitle>
        <DialogDescription>Recorded changes and activity for this user in organizations you can audit, newest first.</DialogDescription>
        {open ? <UserActivity entityId={entityId} userId={userId} /> : null}
        <DialogFooter><Button size="sm" variant="outline" onClick={() => setOpen(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function UserActivity({ entityId, userId }: { entityId: string; userId: string }) {
  const history = useUserAuditHistory(entityId, userId)
  const pager = useLoadMore(history, 'events')
  if (history.isPending) return <LoadingNotice message="Loading user activity…" />
  if (history.isError) {
    if (history.error instanceof ApiError && history.error.status === 403)
      return <p role="alert">You don’t have permission to view this audit log.</p>
    return <RetryNotice message="Couldn’t load user activity." onRetry={() => void history.refetch()} />
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <caption className="sr-only">User audit activity</caption>
          <thead><tr className="border-border border-b">
            {['When', 'Activity', 'Source', 'Actor'].map((label) => <th key={label} scope="col" className="px-3 py-2">{label}</th>)}
          </tr></thead>
          <tbody>
            {pager.items.map((entry) => (
              <tr key={entry.id} className="border-border border-b">
                <td className="whitespace-nowrap px-3 py-2"><time dateTime={entry.timestamp}>{formatDateTime(entry.timestamp)}</time></td>
                <td className="px-3 py-2">{userActivitySummary(entry)}</td>
                <td className="px-3 py-2">{entry.actor.kind === 'scim' ? 'SCIM' : entry.origin}</td>
                <td className="px-3 py-2">{describeActor(entry.actor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!pager.items.length ? <p className="text-muted-foreground py-6 text-center text-xs">No activity recorded for this user.</p> : null}
      </div>
      <LoadMoreButton pager={pager} />
    </>
  )
}

function userActivitySummary(entry: ActivityEntryBase) {
  const roleName = entry.messageParams?.roleName ?? 'the'
  const groupName = entry.messageParams?.groupName ?? 'a group'
  switch (entry.messageKey) {
    case 'activity.user.role-granted': return `Granted ${roleName} role`
    case 'activity.user.role-revoked': return `Revoked ${roleName} role`
    case 'activity.user.added-to-group': return `Added to ${groupName}`
    case 'activity.user.removed-from-group': return `Removed from ${groupName}`
    case 'activity.user.sign-in-linked': return 'Linked sign-in account'
    default: {
      const actionLabels: Readonly<Record<string, string>> = auditLogGridContract.columns.action.labels
      return actionLabels[entry.action] ?? entry.action.charAt(0) + entry.action.slice(1).replaceAll('_', ' ').toLowerCase()
    }
  }
}
